import React, { Suspense, useEffect, useRef } from 'react';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { ActivityIndicator, Platform, useWindowDimensions, View, Text } from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';
import { COLORS } from '../constants/config';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { navigationRef } from '../services/navigationService';

// =====================================================================
// Lazy loading helper for non-critical screens.
// Each wrapped screen gets its own Suspense boundary so React Navigation
// never throws while the chunk is still being fetched. Only use for routes
// that are NOT on the dashboard critical path.
// =====================================================================
function ScreenFallback() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: tokens.colors.raw.white }}>
      <ActivityIndicator size="large" color={COLORS.primary} />
    </View>
  );
}

function lazyScreen<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.ComponentType<any> {
  const LazyComponent = React.lazy(importFn);
  const Wrapped: React.FC<any> = (props) => (
    <Suspense fallback={<ScreenFallback />}>
      <LazyComponent {...props} />
    </Suspense>
  );
  return Wrapped;
}

// =====================================================================
// Eager imports — critical path (dashboard + core CRUD)
// =====================================================================
import DashboardScreen from '../screens/dashboard/DashboardScreen';
import MyOrganizationScreen from '../screens/myorganization/MyOrganizationScreen';
import EditOrganizationScreen from '../screens/myorganization/EditOrganizationScreen';
import EditOrganizationBannerScreen from '../screens/myorganization/EditOrganizationBannerScreen';
import AddOrganizationalUnitScreen from '../screens/myorganization/AddOrganizationalUnitScreen';
import OrganizationalUnitDetailScreen from '../screens/myorganization/OrganizationalUnitDetailScreen';
import AddEmployeeScreen from '../screens/myorganization/AddEmployeeScreen';
import EmployeeDetailScreen from '../screens/myorganization/EmployeeDetailScreen';
import OrganizationsScreen from '../screens/organizations/OrganizationsScreen';
import OrganizationDetailScreen from '../screens/organizations/OrganizationDetailScreen';
import GoalsScreen from '../screens/goals/GoalsScreen';
import GoalDetailScreen from '../screens/goals/GoalDetailScreen';
import AddGoalScreen from '../screens/goals/AddGoalScreen';
import InitiativesScreen from '../screens/initiatives/InitiativesScreen';
import InitiativeDetailScreen from '../screens/initiatives/InitiativeDetailScreen';
import AddInitiativeScreen from '../screens/initiatives/AddInitiativeScreen';
import AddActivityScreen from '../screens/initiatives/AddActivityScreen';
import ActivityDetailScreen from '../screens/initiatives/ActivityDetailScreen';
import UnitGoalsScreen from '../screens/unitgoals/UnitGoalsScreen';
import UnitGoalDetailScreen from '../screens/unitgoals/UnitGoalDetailScreen';
import AddUnitGoalScreen from '../screens/unitgoals/AddUnitGoalScreen';
import ProcessesScreen from '../screens/processes/ProcessesScreen';
import ProcessDetailScreen from '../screens/processes/ProcessDetailScreen';
import SubProcessDetailScreen from '../screens/processes/SubProcessDetailScreen';
import AddProcessScreen from '../screens/processes/AddProcessScreen';
import AddSubProcessScreen from '../screens/processes/AddSubProcessScreen';
import ServicesScreen from '../screens/services/ServicesScreen';
import RisksScreen from '../screens/risks/RisksScreen';
import AddRiskScreen from '../screens/risks/AddRiskScreen';
import EmployeesScreen from '../screens/employees/EmployeesScreen';
import ExportScreen from '../screens/export/ExportScreen';
import KPIsScreen from '../screens/kpis/KPIsScreen';
import KPIDetailScreen from '../screens/kpis/KPIDetailScreen';
import AddInitiativeKPIScreen from '../screens/kpis/AddInitiativeKPIScreen';
import AddKPIReadingScreen from '../screens/kpis/AddKPIReadingScreen';
import AddServiceScreen from '../screens/services/AddServiceScreen';
import AddEvidenceScreen from '../screens/evidence/AddEvidenceScreen';
import ServiceDetailScreen from '../screens/services/ServiceDetailScreen';
import SchedulesScreen from '../screens/schedules/SchedulesScreen';
import SchedulesManagementScreen from '../screens/schedules/SchedulesManagementScreen';
import NotificationsScreen from '../screens/notifications/NotificationsScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import SettingsScreen from '../screens/settings/SettingsScreen';
import SearchResultsScreen from '../screens/search/SearchResultsScreen';
import CustomDrawerContent from '../components/CustomDrawerContent';

import { tokens } from '@design';
import type { RequestType } from '../types';
// Strategy Team Screens
import {
  StrategyDashboardScreen,
  OrganizationsListScreen,
  StrategyOrganizationsScreen,
  PendingApprovalsScreen,
  PerformanceDeviationScreen,
} from '../screens/strategy';

// =====================================================================
// Lazy imports — non-critical routes (evaluation, imports, admin, help).
// These are split out of the initial JS bundle and fetched on first
// navigation. Shaves ~150–250KB gzipped off cold load.
// =====================================================================
const ImportEmployeesScreen = lazyScreen(() => import('../screens/employees/ImportEmployeesScreen'));
const ImportUnitGoalsScreen = lazyScreen(() => import('../screens/unitgoals/ImportUnitGoalsScreen'));
const ImportProcessesScreen = lazyScreen(() => import('../screens/processes/ImportProcessesScreen'));
const ImportInitiativesScreen = lazyScreen(() => import('../screens/initiatives/ImportInitiativesScreen'));
const ImportGoalsScreen = lazyScreen(() => import('../screens/goals/ImportGoalsScreen'));
const ImportServicesScreen = lazyScreen(() => import('../screens/services/ImportServicesScreen'));
const ImportKPIsScreen = lazyScreen(() => import('../screens/kpis/ImportKPIsScreen'));
const ImportRisksScreen = lazyScreen(() => import('../screens/risks/ImportRisksScreen'));
const AdminScreen = lazyScreen(() => import('../screens/admin/AdminScreen'));
const HelpScreen = lazyScreen(() => import('../screens/help/HelpScreen'));
const SupportListScreen = lazyScreen(() => import('../screens/support/SupportListScreen'));
const AddTicketScreen = lazyScreen(() => import('../screens/support/AddTicketScreen'));
const EvaluationDashboard = lazyScreen(() => import('../screens/evaluation/EvaluationDashboard'));
const EvaluationDetail = lazyScreen(() => import('../screens/evaluation/EvaluationDetail'));
const CriteriaScoringForm = lazyScreen(() => import('../screens/evaluation/CriteriaScoringForm'));
const EvaluationReadOnly = lazyScreen(() => import('../screens/evaluation/EvaluationReadOnly'));
const AnnualEvalTable = lazyScreen(() => import('../screens/evaluation/AnnualEvalTable'));
const MonthlyEvalTable = lazyScreen(() => import('../screens/evaluation/MonthlyEvalTable'));
const EvaluationManagement = lazyScreen(() => import('../screens/evaluation/EvaluationManagement'));
const FrameworkBuilder = lazyScreen(() => import('../screens/evaluation/FrameworkBuilder'));


// RTL direction is controlled manually via LanguageContext (isRTL) + utils/rtl.ts.
// Native auto-flip is disabled at app entry in App.tsx.

// Types
export type MainStackParamList = {
  Dashboard: { organizationId?: number } | undefined;
  MyOrganization: { scrollToUnits?: boolean } | undefined;
  EditOrganization: { organizationId: number };
  EditOrganizationBanner: { organizationId?: number } | undefined;
  AddOrganizationalUnit: { unitId?: number; parentId?: number; organizationId?: number; parentName?: string; scrollToKpi?: boolean; preselectGoalId?: number; returnTo?: string; returnKpiId?: string };
  OrganizationalUnitDetail: { id: number };
  AddEmployee: { employeeId?: number; unitId?: number; source?: 'myorganization' | 'employees' };
  EmployeeDetail: { id: number; source?: 'myorganization' | 'employees' };
  Organizations: undefined;
  OrganizationDetail: { id: number };
  Goals: { organizationId?: number };
  GoalDetail: { id: number; fromProcess?: { id: number; name: string }; organizationId?: number; organizationName?: string; orgNavSource?: 'strategy' | 'dashboard' };
  AddGoal: { goalId?: number; organizationId?: number; unitId?: number; scrollToKpi?: boolean; createdUnitId?: number; returnKpiId?: string };
  Initiatives: { goalId?: number; organizationId?: number };
  InitiativeDetail: { id: number };
  AddInitiative: { goalId?: number; initiativeId?: number; scrollToKpi?: boolean };
  AddService: { goalId?: number; serviceId?: number; scrollToKpi?: boolean };
  AddEvidence: { evidenceId?: number; relatedType: string; relatedId: number; contextLabel?: string };
  ServiceDetail: { id: number };
  UnitGoals: { organizationId?: number; organizationName?: string } | undefined;
  UnitGoalDetail: { id: number };
  AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number; scrollToKpi?: boolean };
  Processes: { organizationId?: number; organizationName?: string } | undefined;
  ProcessDetail: { id: number };
  SubProcessDetail: { id: number };
  AddProcess: { processId?: number; scrollToKpi?: boolean; goalId?: number; organizationalUnitId?: number };
  AddSubProcess: { processId?: number; subProcessId?: number; scrollToKpi?: boolean };
  Services: { organizationId?: number; organizationName?: string } | undefined;
  Risks: { successMessage?: string } | undefined;
  AddRisk: { riskId?: number; relatedType?: string; relatedId?: number };
  Employees: { organizationId?: number; organizationName?: string } | undefined;
  ImportEmployees: undefined;
  ImportUnitGoals: undefined;
  ImportProcesses: undefined;
  ImportInitiatives: undefined;
  ImportGoals: undefined;
  ImportServices: undefined;
  ImportKPIs: undefined;
  ImportRisks: undefined;
  Export: undefined;
  Admin: undefined;
  Help: undefined;
  KPIs: { organizationId?: number; goalId?: number; initiativeId?: number };
  KPIDetail: { id: number; organizationId?: number; organizationName?: string; orgNavSource?: 'strategy' | 'dashboard' };
  AddKPIReading: { kpiId: number; readingId?: number };
  AddInitiativeKPI: { initiativeId?: number; kpiId?: number };
  AddActivity: { initiativeId: number; activityId?: number; scrollToKpi?: boolean };
  ActivityDetail: { id: number };
  Schedules: { organizationId?: number };
  SchedulesManagement: undefined;
  Notifications: undefined;
  Profile: undefined;
  Settings: undefined;
  SearchResults: { query?: string };
  // Strategy Team Screens
  StrategyDashboard: undefined;
  StrategyOrganizations: undefined;
  OrganizationsList: undefined;
  PendingApprovals: undefined;
  PerformanceDeviation: undefined;
  // Evaluation Screens
  EvaluationDashboard:
    | {
        cycleId?: number;
        frequency?: 'quarterly' | 'monthly';
        year?: string;
        period?: string;
      }
    | undefined;
  EvaluationDetail: { assignmentId: number; frequency?: 'quarterly' | 'monthly' };
  CriteriaScoringForm: {
    assignmentId: number;
    axisId: number;
    criteriaIndex?: number;
    frequency?: 'quarterly' | 'monthly';
  };
  EvaluationReadOnly: {
    evaluationId: number;
    axisId?: number;
    assignmentId?: number;
    frequency?: 'quarterly' | 'monthly';
  };
  AnnualEvalTable: { departmentId: number };
  MonthlyEvalTable: { departmentId: number };
  EvaluationManagement: { frequency?: 'quarterly' | 'monthly' } | undefined;
  FrameworkBuilder: {
    frameworkId?: number;
    cycleId?: number;
    frequency?: 'quarterly' | 'monthly';
  };
  // Monthly variants — reuse the same components with locked initialParams.
  MonthlyEvaluationManagement: { frequency?: 'monthly' } | undefined;
  MonthlyFrameworkBuilder: {
    frameworkId?: number;
    cycleId?: number;
    frequency?: 'monthly';
  };
};

export type SupportStackParamList = {
  SupportInquiries: { requestType?: RequestType };
  SupportRemarks: { requestType?: RequestType };
  SupportComplaints: { requestType?: RequestType };
  SupportStrategic: { requestType?: RequestType };
  SupportTechnical: { requestType?: RequestType };
  SupportChange: { requestType?: RequestType };
  AddTicket: { requestType?: RequestType };
};

const Drawer = createDrawerNavigator();
const Stack = createNativeStackNavigator<MainStackParamList>();

// Screen options - hide headers (each screen has its own header design)
const stackScreenOptions = {
  headerShown: false,
};

// Stack Navigator for each section
function DashboardStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Dashboard" component={DashboardScreen} />
      <Stack.Screen name="EditOrganization" component={EditOrganizationScreen} />
      {/* Lists reachable from the dashboard's "View Details" cards. Keeping
          them inside the same stack means browser/native Back naturally pops
          to /dashboard/:id (or /dashboard) instead of jumping across stacks
          via the drawer and losing the org-inspection context. */}
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      {/* Org chart on /dashboard/:id deep-links into unit details. Keep these
          inside DashboardStack so executive / evaluator / approver / strategy_team
          (who reach the dashboard by inspecting another org) can drill through
          the org chart the same way org_employee / org_executive do from their
          "My Organization" page. */}
      <Stack.Screen name="OrganizationalUnitDetail" component={OrganizationalUnitDetailScreen} />
      <Stack.Screen name="AddOrganizationalUnit" component={AddOrganizationalUnitScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
      {/* Top-level stat cards (الأهداف / المبادرات / المؤشرات) also need to
          live inside DashboardStack so back-navigation from those lists pops
          to /dashboard/:id instead of jumping stacks. */}
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="ImportGoals" component={ImportGoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="ImportInitiatives" component={ImportInitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="ImportKPIs" component={ImportKPIsScreen} />
      <Stack.Screen name="AddInitiativeKPI" component={AddInitiativeKPIScreen} />
    </Stack.Navigator>
  );
}

function MyOrganizationStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="MyOrganization" component={MyOrganizationScreen} />
      <Stack.Screen name="EditOrganization" component={EditOrganizationScreen} />
      <Stack.Screen name="EditOrganizationBanner" component={EditOrganizationBannerScreen} />
      <Stack.Screen name="AddOrganizationalUnit" component={AddOrganizationalUnitScreen} />
      <Stack.Screen name="OrganizationalUnitDetail" component={OrganizationalUnitDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function OrganizationsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Organizations" component={OrganizationsScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}

function GoalsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="ImportGoals" component={ImportGoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function InitiativesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="ImportInitiatives" component={ImportInitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="AddInitiativeKPI" component={AddInitiativeKPIScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function UnitGoalsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="ImportUnitGoals" component={ImportUnitGoalsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function ProcessesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Processes" component={ProcessesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
      <Stack.Screen name="ImportProcesses" component={ImportProcessesScreen} />
    </Stack.Navigator>
  );
}

function ServicesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function RisksStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Risks" component={RisksScreen} />
      <Stack.Screen name="ImportRisks" component={ImportRisksScreen} />
      <Stack.Screen name="AddRisk" component={AddRiskScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function EmployeesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="ImportEmployees" component={ImportEmployeesScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function ExportStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Export" component={ExportScreen} />
    </Stack.Navigator>
  );
}

function AdminStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Admin" component={AdminScreen} />
    </Stack.Navigator>
  );
}

function HelpStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Help" component={HelpScreen} />
    </Stack.Navigator>
  );
}

const SupportStack = createNativeStackNavigator<SupportStackParamList>();
function SupportStackNavigator() {
  return (
    <SupportStack.Navigator screenOptions={stackScreenOptions}>
      <SupportStack.Screen name="SupportInquiries" component={SupportListScreen} initialParams={{ requestType: 'inquiry' }} />
      <SupportStack.Screen name="SupportRemarks" component={SupportListScreen} initialParams={{ requestType: 'remark' }} />
      <SupportStack.Screen name="SupportComplaints" component={SupportListScreen} initialParams={{ requestType: 'complaint' }} />
      <SupportStack.Screen name="SupportStrategic" component={SupportListScreen} initialParams={{ requestType: 'strategic' }} />
      <SupportStack.Screen name="SupportTechnical" component={SupportListScreen} initialParams={{ requestType: 'technical' }} />
      <SupportStack.Screen name="SupportChange" component={SupportListScreen} initialParams={{ requestType: 'change' }} />
      <SupportStack.Screen name="AddTicket" component={AddTicketScreen} />
    </SupportStack.Navigator>
  );
}

function KPIsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="ImportKPIs" component={ImportKPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

function SchedulesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Schedules" component={SchedulesScreen} />
    </Stack.Navigator>
  );
}

function NotificationsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Notifications" component={NotificationsScreen} />
    </Stack.Navigator>
  );
}

function ProfileStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Profile" component={ProfileScreen} />
    </Stack.Navigator>
  );
}

function SettingsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Settings" component={SettingsScreen} />
    </Stack.Navigator>
  );
}

function EvaluationStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="EvaluationDashboard" component={EvaluationDashboard} />
      <Stack.Screen name="EvaluationDetail" component={EvaluationDetail} />
      <Stack.Screen name="CriteriaScoringForm" component={CriteriaScoringForm} />
      <Stack.Screen name="EvaluationReadOnly" component={EvaluationReadOnly} />
      <Stack.Screen name="AnnualEvalTable" component={AnnualEvalTable} />
      <Stack.Screen name="MonthlyEvalTable" component={MonthlyEvalTable} />
      <Stack.Screen name="EvaluationManagement" component={EvaluationManagement} />
      <Stack.Screen name="FrameworkBuilder" component={FrameworkBuilder} />
      <Stack.Screen
        name="MonthlyEvaluationManagement"
        component={EvaluationManagement}
        initialParams={{ frequency: 'monthly' }}
      />
      <Stack.Screen
        name="MonthlyFrameworkBuilder"
        component={FrameworkBuilder}
        initialParams={{ frequency: 'monthly' }}
      />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
    </Stack.Navigator>
  );
}

function SearchStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="SearchResults" component={SearchResultsScreen} />
      {/* Detail and form screens for deep navigation from search results */}
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
    </Stack.Navigator>
  );
}

// Strategy Organizations Stack (الجهات page for strategy_team users)
function StrategyOrganizationsStack() {
  const { user } = useAuth();
  const navigation = useNavigation();
  const userType = user?.profile?.user_type;

  // Coordinator inherits org_employee scope — treat as org_employee for stack guards.
  useEffect(() => {
    if (userType === 'org_employee' || userType === 'org_executive' || userType === 'coordinator') {
      navigation.dispatch(CommonActions.reset({ index: 0, routes: [{ name: 'DashboardStack' as any }] }));
    }
  }, [userType, navigation]);

  if (userType === 'org_employee' || userType === 'org_executive' || userType === 'coordinator') {
    return null;
  }

  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="StrategyOrganizations" component={StrategyOrganizationsScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="ImportGoals" component={ImportGoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="ImportInitiatives" component={ImportInitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="ImportKPIs" component={ImportKPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}

// Strategy Team Stack (for strategy_team users)
function StrategyStack() {
  const { user } = useAuth();
  const navigation = useNavigation();
  const userType = user?.profile?.user_type;

  // Redirect unauthorized users who reach this stack via direct URL
  // org_executive is allowed for PendingApprovals screen only.
  // Coordinator inherits org_employee scope so block it the same way.
  useEffect(() => {
    if (userType === 'org_employee' || userType === 'coordinator') {
      navigation.dispatch(CommonActions.reset({ index: 0, routes: [{ name: 'DashboardStack' as any }] }));
    }
  }, [userType, navigation]);

  if (userType === 'org_employee' || userType === 'coordinator') {
    return null;
  }

  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="StrategyDashboard" component={StrategyDashboardScreen} />
      <Stack.Screen name="OrganizationsList" component={OrganizationsListScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="PendingApprovals" component={PendingApprovalsScreen} />
      <Stack.Screen name="PerformanceDeviation" component={PerformanceDeviationScreen} />
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="ImportGoals" component={ImportGoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="ImportInitiatives" component={ImportInitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ImportServices" component={ImportServicesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="ImportKPIs" component={ImportKPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddEvidence" component={AddEvidenceScreen} />
      <Stack.Screen name="Schedules" component={SchedulesScreen} />
      <Stack.Screen name="SchedulesManagement" component={SchedulesManagementScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}


export default function MainNavigator() {
  const { width } = useWindowDimensions();
  const { user, isLoading } = useAuth();
  const { isVisible: isSidebarVisible } = useSidebar();
  const { colors } = useTheme();
  const { t } = useLanguage();

  // Get user role
  const userRole = user?.profile?.user_type || '';

  // Track if we've already redirected to prevent loops
  const hasRedirected = useRef(false);

  // Redirect executive users to StrategyStack ONLY when they land on a stack
  // they have no business being on (e.g. DashboardStack). Without this guard,
  // a browser refresh on `/export`, `/goals`, `/strategy/...` (any URL the
  // linking config rehydrates correctly) would be undone here — wiping the
  // restored navigation state and dumping the user back at `/strategy`.
  useEffect(() => {
    if (userRole !== 'executive' || hasRedirected.current || !navigationRef.isReady()) {
      return;
    }
    const state = navigationRef.getRootState();
    const currentRoute = state?.routes?.[state.index]?.name;
    // Only redirect from the root dashboard stack — executive shouldn't land
    // there. Every other top-level stack is a legitimate destination they may
    // have refreshed into.
    if (currentRoute === 'DashboardStack') {
      hasRedirected.current = true;
      navigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'StrategyStack' }],
        })
      );
    }
  }, [userRole]);

  // Determine initial route based on user role
  // superuser -> StrategyOrganizationsStack (Admin interface: Organizations + User Management only)
  // strategy_team, executive -> StrategyStack (executive is VIEW ONLY)
  // org_employee -> DashboardStack
  const getInitialRouteName = () => {
    if (userRole === 'superuser') {
      return 'StrategyOrganizationsStack'; // Admin goes directly to Organizations
    }
    if (
      userRole === 'strategy_team'
      || userRole === 'executive'
      || userRole === 'performance_evaluator'
      || userRole === 'evaluation_approver'
    ) {
      return 'StrategyStack';
    }
    return 'DashboardStack';
  };

  // Responsive breakpoints
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;

  // Use permanent drawer on web for larger screens, front drawer for smaller screens
  const drawerType = Platform.OS === 'web'
    ? (isSmallScreen ? 'front' : 'permanent')
    : 'front';

  // Responsive drawer width - set to 0 when sidebar is hidden (web only)
  // On small screens: cap at 85% of screen width for very small phones, otherwise 260px
  const baseDrawerWidth = isSmallScreen ? Math.min(Math.round(width * 0.85), 260) : (isMediumScreen ? 220 : 240);
  const drawerWidth = Platform.OS === 'web' && !isSidebarVisible && !isSmallScreen ? 0 : baseDrawerWidth;

  // IMPORTANT: Wait for user data to load before rendering navigator
  // This ensures the correct initialRouteName is set based on actual user role
  // Also wait for user.profile.user_type to be available
  if (isLoading || !user || !user.profile || !user.profile.user_type) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
        <Text style={{ fontSize: tokens.typography.paragraph["paragraph font size"], color: colors.textMuted }}>{t('common.loading')}</Text>
      </View>
    );
  }

  // Use key to force re-mount navigator when user role changes
  // This ensures correct initialRouteName is applied
  const navigatorKey = `navigator-${userRole}-${user?.id || 'unknown'}`;

  return (
    <Drawer.Navigator
      key={navigatorKey}
      initialRouteName={getInitialRouteName()}
      drawerContent={(props) => <CustomDrawerContent {...props} />}
      screenOptions={{
        headerShown: false,
        drawerPosition: 'right', // RTL
        drawerStyle: {
          backgroundColor: colors.card,
          width: drawerWidth,
        },
        drawerType: drawerType,
        swipeEnabled: Platform.OS !== 'web' || isSmallScreen,
      }}
    >
      {/* Strategy Team Stack - also used by executive (VIEW ONLY) */}
      <Drawer.Screen name="StrategyStack" component={StrategyStack} />

      {/* Main Navigation Items - matching sidebar order */}
      <Drawer.Screen name="DashboardStack" component={DashboardStack} />
      <Drawer.Screen name="MyOrganizationStack" component={MyOrganizationStack} />
      <Drawer.Screen name="GoalsStack" component={GoalsStack} />
      <Drawer.Screen name="InitiativesStack" component={InitiativesStack} />
      <Drawer.Screen name="UnitGoalsStack" component={UnitGoalsStack} />
      <Drawer.Screen name="ProcessesStack" component={ProcessesStack} />
      <Drawer.Screen name="ServicesStack" component={ServicesStack} />
      <Drawer.Screen name="EvaluationStack" component={EvaluationStack} />
      <Drawer.Screen name="RisksStack" component={RisksStack} />
      <Drawer.Screen name="EmployeesStack" component={EmployeesStack} />
      <Drawer.Screen name="ExportStack" component={ExportStack} />
      <Drawer.Screen name="AdminStack" component={AdminStack} />
      <Drawer.Screen name="HelpStack" component={HelpStack} />
      <Drawer.Screen name="SupportStack" component={SupportStackNavigator} />

      {/* Hidden screens (not in sidebar but accessible) */}
      <Drawer.Screen name="OrganizationsStack" component={OrganizationsStack} />
      <Drawer.Screen name="SchedulesStack" component={SchedulesStack} />
      <Drawer.Screen name="NotificationsStack" component={NotificationsStack} />
      <Drawer.Screen name="ProfileStack" component={ProfileStack} />
      <Drawer.Screen name="SettingsStack" component={SettingsStack} />
      <Drawer.Screen name="SearchStack" component={SearchStack} />
      <Drawer.Screen name="StrategyOrganizationsStack" component={StrategyOrganizationsStack} />
    </Drawer.Navigator>
  );
}
